import { ReactElement, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { Page, notification, CrudMode } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType, ChangeRequestResponse, changeRequestGetLatestChangeRequests } from '@pmp/api';
import {
  CrudTable,
  getColumnsByEntityType,
  ChangeRequestsList,
  ChangeRequestsDeleteModal,
  ChangeRequestsApplyModal,
} from '@pmp/components';
import { useAdditionalFieldsStore, useValueListsStore } from '@pmp/stores';
import { useFeatureToggle } from '@pmp/hooks';

import { openCrud } from './utils';
import { initialState, reducer } from './reducer';

const VALUE_LIST_NAMES: FieldType[] = [FieldType.ACTION_ENTITY_TYPE, FieldType.ACTION_TYPE, FieldType.ACTION_STATUS];

export const ChangeRequestsPage = (): ReactElement => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { valueLists, getValueLists } = useValueListsStore();
  const { getAdditionalFields } = useAdditionalFieldsStore();
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const getChangeRequests = useCallback(
    () =>
      changeRequestGetLatestChangeRequests()
        .then(({ data }: { data: ChangeRequestResponse[] }) => {
          dispatch({ type: 'setDataSource', dataSource: data });
        })
        .catch(() => notification().error({ message: t('global.errors.unexpected') })),
    [t],
  );

  const refreshTable = useCallback(() => {
    dispatch({ type: 'setIsLoading', isLoading: true });
    return getChangeRequests().finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
  }, [getChangeRequests]);

  useEffect(() => {
    getValueLists({ fetchIfMissing: VALUE_LIST_NAMES });
    refreshTable();
  }, [getValueLists, refreshTable]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (state.crudTable.isOpen) return;
      getChangeRequests();
    }, 15000);

    return () => clearInterval(interval);
  }, [getChangeRequests, state.crudTable.isOpen]);

  const onSetDataSource = useCallback((data: ChangeRequestResponse[]) => {
    dispatch({ type: 'setDataSource', dataSource: data });
  }, []);

  const updateTable = useCallback((dataSourceRow: Partial<ChangeRequestResponse>) => {
    dispatch({ type: 'updateDataSource', dataSourceRow });
  }, []);

  const onView = useCallback(
    (record: ChangeRequestResponse, mode: CrudMode) => {
      if (!record.actionId || !record.actionEntityType.key || !record.status.key) return;
      openCrud({
        record,
        mode,
        status: undefined,
        getValueLists,
        getAdditionalFields,
        dispatch,
        notification,
        t,
      });
    },
    [getAdditionalFields, getValueLists, t],
  );

  const onReview = useCallback(
    (record: ChangeRequestResponse) => {
      if (!record.actionId || !record.actionEntityType.key) return;
      openCrud({
        record,
        mode: CrudMode.REVIEW,
        status: undefined,
        getValueLists,
        getAdditionalFields,
        dispatch,
        notification,
        t,
      });
    },
    [getAdditionalFields, getValueLists, t],
  );

  const onEdit = useCallback(
    (record: ChangeRequestResponse) => {
      if (!record.actionId || !record.actionEntityType.key) return;
      openCrud({
        record,
        mode: record.actionType.key === 'IMPORT' ? CrudMode.UPDATE_IMPORT_CHANGE : CrudMode.UPDATE_CHANGE,
        getValueLists,
        getAdditionalFields,
        dispatch,
        notification,
        t,
      });
    },
    [getAdditionalFields, getValueLists, t],
  );

  const onApply = useCallback((record: ChangeRequestResponse) => {
    dispatch({ type: 'openApplyModal', record });
  }, []);

  const onCloseAppyModal = useCallback(() => {
    dispatch({ type: 'closeApplyModal' });
  }, []);

  const onOpenDeleteModal = useCallback((record: ChangeRequestResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrudTable' });
  }, []);

  const crudTableProps = useMemo(() => {
    const entityType = state.crudTable.changeRequestData?.actionEntityType.key as ActionEntityType | null;

    return {
      ...state.crudTable,
      columns: getColumnsByEntityType(entityType),
      additionalFields: (entityType && state.entitiesAdditionalFields?.[entityType]) ?? {},
      valueLists,
      updateTable,
      onClose: onCloseCrud,
    };
  }, [onCloseCrud, state.crudTable, state.entitiesAdditionalFields, updateTable, valueLists]);

  const title = useMemo(() => {
    return enableFourEyes ? t('global.titles.changeRequests') : t('global.titles.changeTracker');
  }, [t, enableFourEyes]);

  return (
    <Page>
      <Page.Title>{title}</Page.Title>
      <Page.Content>
        <ChangeRequestsList
          state={state}
          valueLists={valueLists}
          onSetDataSource={onSetDataSource}
          onView={onView}
          onReview={onReview}
          onEdit={onEdit}
          onApply={onApply}
          onDelete={onOpenDeleteModal}
        />
        {state.crudTable.isOpen && <CrudTable {...crudTableProps} />}
      </Page.Content>
      <ChangeRequestsDeleteModal
        open={state.deleteModal.open}
        record={state.deleteModal.record}
        onClose={onCloseDeleteModal}
        refreshTable={refreshTable}
      />
      <ChangeRequestsApplyModal
        open={state.applyModal.open}
        record={state.applyModal.record}
        onClose={onCloseAppyModal}
        refreshTable={refreshTable}
      />
    </Page>
  );
};
